import { useEffect, useState } from 'react';

import {
  runOnJS,
  useAnimatedReaction,
  useDerivedValue,
  useSharedValue,
  withDelay,
  withTiming,
} from 'react-native-reanimated';

import {
  MAX_ANIMATION_DURATION,
  MIN_ANIMATION_DURATION,
  PROGRESS_ANIMATION_CONFIG,
} from 'pages/loyalty/loyalty-profile/profile-card/components/animation-utils';
import { HapticsNotificationType, hapticNotification } from 'utils/haptic';

interface UseProgressAnimationProps {
  currentPoints: number;
  lastSeenLoyaltyPoints: number;
  shouldAnimate: boolean;
  startValue: number;
  targetValue: number;
}

export const useProgressAnimation = ({
  currentPoints,
  lastSeenLoyaltyPoints,
  shouldAnimate,
  startValue,
  targetValue,
}: UseProgressAnimationProps) => {
  const [displayedPoints, setDisplayedPoints] = useState<number>(lastSeenLoyaltyPoints);
  const [showConfettiAnimation, setShowConfettiAnimation] = useState(false);

  // Shared values
  const progressSV = useSharedValue(lastSeenLoyaltyPoints);
  const lastEmittedSV = useSharedValue(Math.floor(lastSeenLoyaltyPoints));
  const widthSV = useSharedValue(shouldAnimate ? startValue : targetValue);

  // Derived value to avoid reading from shared value during render
  const progressFloor = useDerivedValue(() => Math.floor(progressSV.value));

  // Calculate duration based on points difference
  const pointsDifference = currentPoints - lastSeenLoyaltyPoints;
  const animationDuration = Math.max(
    MIN_ANIMATION_DURATION,
    Math.min(
      MAX_ANIMATION_DURATION,
      (pointsDifference / PROGRESS_ANIMATION_CONFIG.pointsPerSec) * 1000 // to ms
    )
  );

  useEffect(() => {
    setShowConfettiAnimation(false);

    if (!shouldAnimate) {
      progressSV.value = currentPoints;
      lastEmittedSV.value = Math.floor(currentPoints);
      setDisplayedPoints(currentPoints);
      widthSV.value = targetValue;
      return;
    }

    // Initialize values
    progressSV.value = lastSeenLoyaltyPoints;
    lastEmittedSV.value = Math.floor(lastSeenLoyaltyPoints);
    widthSV.value = startValue;

    // Animate progress bar with calculated duration
    progressSV.value = withDelay(
      PROGRESS_ANIMATION_CONFIG.startDelay,
      withTiming(currentPoints, {
        duration: animationDuration,
        easing: PROGRESS_ANIMATION_CONFIG.easing,
      })
    );

    // Animate width bar with same duration
    widthSV.value = withDelay(
      PROGRESS_ANIMATION_CONFIG.startDelay,
      withTiming(
        targetValue,
        {
          duration: animationDuration,
          easing: PROGRESS_ANIMATION_CONFIG.easing,
        },
        finished => {
          if (finished) {
            runOnJS(hapticNotification)({ type: HapticsNotificationType.SUCCESS });
            runOnJS(setShowConfettiAnimation)(true);
          }
        }
      )
    );
  }, [
    shouldAnimate,
    currentPoints,
    lastSeenLoyaltyPoints,
    progressSV,
    lastEmittedSV,
    widthSV,
    startValue,
    targetValue,
    animationDuration,
  ]);

  // Text animation (shows intermediate integers)
  useAnimatedReaction(
    () => progressFloor.value,
    val => {
      'worklet';
      if (val > lastEmittedSV.value) {
        lastEmittedSV.value = val;
        runOnJS(setDisplayedPoints)(val);
      }
    }
  );

  return {
    displayedPoints,
    showConfettiAnimation,
    progressSV,
    widthSV,
    animationDuration,
  };
};
